import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { format, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AgentCreatedByFilter from './AgentCreatedByFilter.tsx';
import AgentSenderFilter from './AgentSenderFilter.tsx';
import AgentStatusFilter from './AgentStatusFilter.tsx';
import DateRangePresetFilter from '../../../../core/components/DateRangePresetFilter.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { isoDateFormat } from '../../../../core/utils/dates.ts';
import { IFilter, SEARCH_FILTER_KEY } from '../../../../core/utils/filterQuery.ts';
import { useDebounce } from '../../../../core/utils/hooks/useDebounce';

const SearchIcon = withIcon(faMagnifyingGlass);

interface AssistantActivityToolbarProps {
    filters: IFilter[];
    onFiltersChange: (filters: IFilter[]) => void;
    creators: any[];
    senders: string[];
}

const toDateRange = (from: Date, to: Date) => [format(from, isoDateFormat), format(to, isoDateFormat)].join(',');

const AssistantActivityToolbar = ({ filters, onFiltersChange, creators, senders }: AssistantActivityToolbarProps) => {
    const { t } = useTranslation('agents', { keyPrefix: 'historyPage.filters' });

    let searchTerm = filters.find((f) => f.name === SEARCH_FILTER_KEY)?.value || '';
    if (Array.isArray(searchTerm)) searchTerm = searchTerm[0];
    const [searchInputValue, setSearchInputValue] = useState(searchTerm);

    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const propagateSearchFilter = useCallback(
        (value: string) => {
            const newFilters = filtersRef.current.filter((f) => f.name !== SEARCH_FILTER_KEY);
            if (value) {
                newFilters.push({ name: SEARCH_FILTER_KEY, value, exclude: false, expression: 'eq' });
            }
            onFiltersChange(newFilters);
        },
        [onFiltersChange]
    );
    const debouncedPropagateSearchFilter = useDebounce(propagateSearchFilter, 500);

    useEffect(() => {
        setSearchInputValue(searchTerm);
    }, [searchTerm]);

    const today = new Date();
    const datePresetOptions = [
        { label: t('createdAt.options.today'), value: toDateRange(today, today) },
        { label: t('createdAt.options.lastWeek'), value: toDateRange(subWeeks(today, 1), today) },
        { label: t('createdAt.options.lastMonth'), value: toDateRange(subMonths(today, 1), today) },
        { label: t('createdAt.options.last3Months'), value: toDateRange(subDays(today, 90), today) },
        { label: t('createdAt.options.lastYear'), value: toDateRange(subYears(today, 1), today) },
    ];

    const hasFilters = filters.some((f) => f.name !== SEARCH_FILTER_KEY);

    return (
        <div className="flex flex-wrap items-center gap-2">
            <AgentSenderFilter filters={filters} onFiltersChange={onFiltersChange} senders={senders} />
            <AgentStatusFilter filters={filters} onFiltersChange={onFiltersChange} />
            <AgentCreatedByFilter filters={filters} onFiltersChange={onFiltersChange} creators={creators} />
            <DateRangePresetFilter
                filters={filters}
                onFiltersChange={onFiltersChange}
                filterName="created_at"
                label={t('createdAt.label')}
                presetOptions={datePresetOptions}
                maxDate={today}
            />
            {hasFilters && (
                <button
                    type="button"
                    className="rounded px-3 py-1.5 text-sm font-medium text-content-strong transition-colors hover:bg-surface-strong"
                    onClick={() => onFiltersChange(filters.filter((f) => f.name === SEARCH_FILTER_KEY))}
                >
                    {t('clearAll')}
                </button>
            )}
            <StringField
                className="ml-auto w-full sm:w-64"
                prefix={<SearchIcon />}
                value={searchInputValue}
                onValueChange={(value: string) => {
                    setSearchInputValue(value);
                    debouncedPropagateSearchFilter(value);
                }}
                placeholder={t('search.placeholder')}
                aria-label={t('search.placeholder')}
            />
        </div>
    );
};

export default AssistantActivityToolbar;
